/**
 * The pieces every mission generator shares: the seeded draw, the flight
 * builder, and the briefing prose.
 *
 * All randomness comes from one `mission` stream seeded off the mission seed
 * (`flight/rng.ts`), and it is all consumed here at *setup* time — the sim
 * itself never draws from it. That is what makes a `MissionSpec` reproducible:
 * same seed, same aeroplanes, same weather, same sky.
 *
 * The briefing sentences are assembled from `PS.EXE`'s own template strings,
 * quoted in docs/game/missions.md § *Mission setup parameters*, with the same
 * slots filled in the same order.
 */

import type { AircraftKey } from "../flight/aircraft-table.ts";
import type { BombLoadSpec, EncounterPilot, EncounterSide } from "../combat/encounter.ts";
import type { RouteOrders, Waypoint } from "../combat/route.ts";
import type { AmmunitionType } from "../combat/targets.ts";
import { RngBank, createRngBankState } from "../flight/rng.ts";
import { fnv1a32 } from "../flight/hash.ts";
import type { Vec3 } from "../flight/math3d.ts";
import type { AiSkill } from "../combat/skill.ts";
import type { MissionBase, MissionFlight } from "./mission-spec.ts";
import { aircraftLabel, nationOf, type Nation } from "./mission-roster.ts";
import {
  SKY_CONDITIONS,
  TIMES_OF_DAY,
  WIND_STRENGTHS,
  REGION_LABELS,
  type MissionRegion,
  type MissionWeather,
} from "./mission-world.ts";

/** A seeded draw over the `mission` stream. Setup-time only. */
export class MissionDraw {
  private readonly bank: RngBank;

  constructor(seed: number, salt: string) {
    this.bank = new RngBank(createRngBankState(fnv1a32(salt, seed >>> 0)));
  }

  float(): number {
    return this.bank.nextFloat("mission");
  }

  /** Uniform in `[min, max)`. */
  between(min: number, max: number): number {
    return min + this.float() * (max - min);
  }

  /** Uniform integer in `[min, max]`. */
  int(min: number, max: number): number {
    return min + Math.floor(this.float() * (max - min + 1));
  }

  pick<T>(items: readonly T[]): T {
    const chosen = items[Math.floor(this.float() * items.length)] ?? items[0];
    if (chosen === undefined) throw new Error("cannot draw from an empty list");
    return chosen;
  }

  chance(probability: number): boolean {
    return this.bank.chance("mission", probability);
  }

  /** −1 or +1: which way a patrol leg or a bounce goes. */
  side(): number {
    return this.chance(0.5) ? 1 : -1;
  }
}

/**
 * The sky and the clock are drawn but not *simulated* — this port has no cloud
 * or lighting model, so they are real briefing text over a stub and the select
 * screen says so rather than implying the sky will look different.
 *
 * The **wind** word is no longer in that category, and since AD-007k §10 it is
 * not decorative either: the original's wind model was recovered out of
 * `RB.EXE`, `mission-spec.ts` stamps this word on every aeroplane in the
 * encounter, and `wind.ts`'s severity table gives each word its own strength.
 * So a briefing that says *"the winds are still"* is telling the truth, and one
 * that says *"strong"* is a warning. The draw is uniform over the three words
 * `PS.EXE` exposes, which is a third of sorties in dead air and a third in the
 * 9 m/s that costs a bomb run its margin.
 *
 * `overrides` is roadmap 11s's Weather Selection picker: the original boxes
 * all three of these words in its Mission Setup sentence exactly like the
 * aeroplane and the region — `re-sessions/calib/shots/nav-setup.png` and
 * `nav-windpopup.png` show `[daytime]`, `[light]` and `[partly cloudy]` boxed
 * the same way as `[Sopwith Pups]` and `[echelon]`, and clicking the winds box
 * opens a `CHOOSE` popup of exactly `still`/`light`/`strong`/`CANCEL`
 * (`flight-tunables.md` §8.5.5). So a caller may pin any subset of the three
 * fields. An unpinned field is still drawn from `draw`, in the same order as
 * before this parameter existed, so a call with no overrides is
 * byte-identical to the old `drawWeather(draw)` and the zero-diff invariant
 * holds.
 */
export function drawWeather(draw: MissionDraw, overrides?: Partial<MissionWeather>): MissionWeather {
  return {
    sky: overrides?.sky ?? draw.pick(SKY_CONDITIONS),
    wind: overrides?.wind ?? draw.pick(WIND_STRENGTHS),
    // Night and dusk are drawn far less often than daylight: the port has no
    // lighting model, so a night sortie would read exactly like a noon one.
    timeOfDay: overrides?.timeOfDay ?? (draw.chance(0.8) ? "daytime" : draw.pick(TIMES_OF_DAY)),
  };
}

/** Port-side: `PS.EXE`'s `%i` sun slot has no enumerated value set in any decoded file. */
export function sunPositionOf(weather: MissionWeather): string {
  if (weather.timeOfDay === "dawn") return "low in the east";
  if (weather.timeOfDay === "dusk") return "low in the west";
  if (weather.timeOfDay === "night") return "below the horizon";
  return "high overhead";
}

export interface FlightSeed {
  readonly id: string;
  readonly side: EncounterSide;
  readonly aircraft: AircraftKey;
  readonly callsign: string;
  readonly position: Vec3;
  readonly headingRad: number;
  readonly skill?: AiSkill;
  readonly waypoints?: readonly Waypoint[];
  readonly routeOrders?: RouteOrders;
  readonly ammunition?: AmmunitionType;
  readonly airspeedMps?: number;
  readonly onGround?: boolean;
  readonly bombs?: BombLoadSpec;
  readonly strikeTargetIds?: readonly string[];
}

/** `skill` omitted makes this the player's aeroplane. */
export function buildFlight(seed: FlightSeed): MissionFlight {
  const pilot: EncounterPilot = seed.skill ? { kind: "ai", skill: seed.skill } : { kind: "player" };
  return {
    id: seed.id,
    side: seed.side,
    nation: nationOf(seed.aircraft),
    aircraft: seed.aircraft,
    pilot,
    callsign: seed.callsign,
    position: seed.position,
    headingRad: seed.headingRad,
    onGround: seed.onGround,
    airspeedMps: seed.airspeedMps,
    waypoints: seed.waypoints,
    routeOrders: seed.routeOrders,
    ammunition: seed.ammunition,
    bombs: seed.bombs,
    strikeTargetIds: seed.strikeTargetIds,
  };
}

export function waypoint(position: Vec3, label: string): Waypoint {
  return { position, label };
}

/**
 * The first leg of any aerodrome start (Scramble's own, and every other
 * generator's `MissionOptions.aerodromeStart` — `mission-options.ts`): climb
 * out over the field before turning for wherever the mission actually starts.
 */
export function climbFrom(base: MissionBase, altitudeM: number): Waypoint {
  return waypoint({ x: base.position.x, y: altitudeM, z: base.position.z }, "CLIMB");
}

/**
 * The plain-English sentence an aerodrome-start briefing opens with — the
 * manual's own framing (`04-gameplay.md`: *"You are starting at Vousiers
 * aerodrome"*), not a recovered `PS.EXE` template: no per-mission-type
 * template string for this line is documented anywhere in the corpus.
 */
export function aerodromeStartLine(base: MissionBase): string {
  return `You are starting at ${base.name} aerodrome.`;
}

/** `PS.EXE`'s `%h %y the enemy` slot pair. */
export function altitudeRelation(ownM: number, enemyM: number): string {
  const difference = ownM - enemyM;
  if (Math.abs(difference) < 150) return "the same altitude as";
  return difference > 0 ? "a higher altitude than" : "a lower altitude than";
}

export interface BriefingInput {
  readonly flightSize: number;
  readonly ownAircraft: AircraftKey;
  readonly enemyCount: number;
  readonly enemySkill: string;
  readonly enemyNation: Nation;
  readonly enemyLeader: string;
  readonly enemyAircraft: AircraftKey;
  readonly region: MissionRegion;
  readonly ownAltitudeM: number;
  readonly enemyAltitudeM: number;
  readonly weather: MissionWeather;
}

function plural(count: number, singular: string): string {
  return count === 1 ? `one ${singular}` : `${count} ${singular}s`;
}

/**
 * The three-sentence briefing `PS.EXE` builds from
 * `"Your flight of %a will fly %b fighters against %c %z %d, led by %e, flying
 * %f fighters."` / `"Your flight will start over %g, at %h %y the enemy."` /
 * `"The sun is at %i. The winds are %j. The sky is %k."`
 */
export function briefingLines(input: BriefingInput): string[] {
  const nationWord = input.enemyNation === "german" ? "German" : "Allied";
  return [
    `Your flight of ${input.flightSize} will fly ${aircraftLabel(input.ownAircraft)} fighters against ${plural(input.enemyCount, `${input.enemySkill} ${nationWord} pilot`)}, led by ${input.enemyLeader}, flying ${aircraftLabel(input.enemyAircraft)} fighters.`,
    `Your flight will start over ${REGION_LABELS[input.region]}, at ${altitudeRelation(input.ownAltitudeM, input.enemyAltitudeM)} the enemy.`,
    `The sun is ${sunPositionOf(input.weather)}. The winds are ${input.weather.wind}. The sky is ${input.weather.sky}.`,
  ];
}

/** The Dogfight-a-Famous-Ace variant of the same template. */
export function aceBriefingLines(
  ownAircraft: AircraftKey,
  aceAircraft: AircraftKey,
  region: MissionRegion,
  ownAltitudeM: number,
  aceAltitudeM: number,
  weather: MissionWeather,
): string[] {
  return [
    `You're flying a ${aircraftLabel(ownAircraft)} against his ${aircraftLabel(aceAircraft)}.`,
    `You'll face off over ${REGION_LABELS[region]}, at ${altitudeRelation(ownAltitudeM, aceAltitudeM)} the enemy.`,
    `The sun is ${sunPositionOf(weather)}. The winds are ${weather.wind}. The sky is ${weather.sky}.`,
  ];
}
